import * as d3 from 'd3';

import asm from 'assembly/Asm';
import * as vo from 'components/VisibilityOptions';
import PolymerAxisView from 'molbio/maps/linear/PolymerAxisView';
import Type from 'molbio/maps/Type';
import * as sequtil from 'molbio/SeqUtil';
import throttle from 'util/throttle';

class SequenceAxisView extends PolymerAxisView {
  static initClass(): void {
    this.prototype.className = 'sequenceAxis';
    this.prototype.setting = vo.SEQUENCE_AXIS.name;
  }

  getBasesType(): Type {
    return this.model.get('layout').DNA.get('type');
  }

  registerListeners(): void {
    super.registerListeners();
    // This listener only applies to the MiniMap. The parent class handles triggering a re-render for other
    // linear axes when the range of the slice changes, but that doesn't apply for the MiniMap axis so we need
    // this listener.
    this.listenTo(
      this.slice,
      'change:leftBP change:rightBP',
      throttle(() => {
        this.update(false);
      }, 50)
    );
    const _sequence = this.model.get('sequence')._sequence();
    this.listenTo(_sequence, 'change:startIndex', () => {
      this.update(false);
    });
  }

  _drawAssemblyFragments(): void {
    const _sequence = this.model.get('sequence')._sequence();
    const pieces = asm.of(_sequence).getFragmentResultPieces(_sequence);
    const sliceLength = sequtil.length(this.slice.start(), this.slice.end(), _sequence.length());
    for (const [color, start, end] of pieces) {
      const overlaps = sequtil.getRangeOverlap(start, end, this.slice.start(), this.slice.end());
      if (overlaps.length > 0) {
        for (const overlap of Array.from(overlaps)) {
          let translateLength;
          const [overlapStart, overlapEnd] = Array.from(overlap);
          const overlapLength = sequtil.length(overlapStart, overlapEnd, _sequence.length());
          const ratio = overlapLength / sliceLength;
          if (this.slice.start() === overlapStart) {
            translateLength = 0;
          } else {
            translateLength = sequtil.length(this.slice.start(), overlapStart, _sequence.length());
          }
          const translateRatio = translateLength / sliceLength;
          const width = this.slice.get('width')! * ratio;
          const translateWidth = this.slice.get('width')! * translateRatio - this.getTypeOffset();
          this.getAxisRoot()
            .append('path')
            .attr('d', `M 0,0 L ${width},0`)
            .style('stroke', color)
            .style('stroke-width', 2)
            .attr('transform', `translate(${translateWidth}, 0)`)
            .classed('linear-axis-piece', true);
        }
      }
    }
    const startIndex = _sequence.startIndex();
    const getPiece = (pos: number): [string, number, number] | undefined => {
      for (const piece of pieces) {
        const [_, start, end] = piece;
        if (sequtil.baseInRange(sequtil.displayToModel(pos, startIndex), start, end)) {
          return piece;
        }
      }
      return undefined;
    };

    d3.select(this.el)
      .selectAll('.axis .tick line')
      .style('stroke-width', (d: number) => {
        const piece = getPiece(d);
        if (piece != null) {
          return 2;
        } else {
          return 1;
        }
      })
      .style('stroke', (d: number) => {
        const piece = getPiece(d);
        if (piece != null) {
          return piece[0];
        } else {
          return 'black';
        }
      });
  }

  _performAdditionalUpdates(): void {
    if (!this.model.get('sequence').finalized()) {
      this._drawAssemblyFragments();
    } else {
      d3.select(this.el).selectAll('.axis .tick line').style('stroke-width', 1).style('stroke', 'black');
    }
  }
}
SequenceAxisView.initClass();
export default SequenceAxisView;
